// Always-visible footer with the current final guess: lat/lng,
// region label + source, copy-to-clipboard button, and a small
// embedded OpenStreetMap preview. The Maps button used to open
// google.com/maps in the user's default browser — on a side-by-side
// ultrawide setup that meant a tab landed next to GeoGuessr, which
// is the exact anti-cheat leak vector we're trying to avoid. The
// inline mini-map gives the same "where did Atlas pin this"
// information without touching the user's browser at all.

import { copyFinalWithToast } from "../actions/copyFinal";
import { useStore } from "../state/store";
import { outputClassFor } from "../types/contract";
import { MiniMap } from "./MiniMap";
import { TruthRecorder } from "./TruthRecorder";

export function FinalGuessFooter() {
  const final = useStore((s) => s.finalGuess);
  const mode = useStore((s) => s.gameMode);
  const roundId = useStore((s) => s.currentRoundId);
  const outputClass = outputClassFor(mode);

  return (
    <div
      data-testid="final-footer"
      className="border-t border-white/5 px-3 py-2 flex flex-col gap-2"
    >
      {final ? (
        outputClass === "country_only" ? (
          // Country-only modes: show only the region label (which the
          // ensemble fills with the country name). Hide lat/lng and
          // the Leaflet map — both would be misleading at country
          // granularity, and the user pastes the country name into
          // GeoGuessr's country picker, not coords.
          <div className="flex items-center gap-2">
            <div className="flex flex-col">
              <div className="font-semibold text-base">{final.label}</div>
              <div className="text-xs text-fg-muted">
                {final.source}
                {typeof final.confidence === "number" && (
                  <> · {(final.confidence * 100).toFixed(0)}%</>
                )}
              </div>
            </div>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => void copyFinalWithToast()}
              className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
            >
              📋 Copy
            </button>
            {roundId && <TruthRecorder roundId={roundId} mode={mode} />}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <div className="font-semibold tabular-nums">
                  {final.lat.toFixed(5)}, {final.lng.toFixed(5)}
                </div>
                <div className="text-xs text-fg-muted">
                  {final.label} · {final.source}
                  {typeof final.confidence === "number" && (
                    <> · {(final.confidence * 100).toFixed(0)}%</>
                  )}
                </div>
              </div>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => void copyFinalWithToast()}
                className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
              >
                📋 Copy
              </button>
            </div>
            <MiniMap lat={final.lat} lng={final.lng} />
          </>
        )
      ) : (
        <div className="text-fg-muted text-xs">No guess yet — capture a round.</div>
      )}
    </div>
  );
}
